package com.fireavert.yardi.common_data.client

import YardiSavePropertyDataRequest
import com.fireavert.cache.client.Preferences
import com.fireavert.common.ApiRepository
import com.fireavert.common.Try
import com.fireavert.user.data_models.TokenRefreshService
import com.fireavert.yardi.common_data.data_models.requests.client_requests.*
import com.fireavert.yardi.common_data.data_models.responses.PropertyWithUnitsData
import com.fireavert.yardi.common_data.data_models.responses.YardiImportUnitDataResponse
import com.fireavert.yardi.common_data.data_models.responses.YardiPropertyListItemData
import com.fireavert.yardi.service_requests.data_models.requests.client_requests.YardiClientServiceRequestCreateEditRequest
import com.fireavert.yardi.service_requests.data_models.requests.client_requests.YardiClientServiceRequestGetRequest
import com.fireavert.yardi.service_requests.data_models.responses.YardiServiceRequestCreateEditResponse
import com.fireavert.yardi.service_requests.data_models.responses.YardiServiceRequestGetResponse
import com.fireavert.yardi.service_requests.data_models.responses.YardiServiceRequestPropertyConfigurationsResponse
import com.fireavert.yardi.service_requests.data_models.responses.YardiServiceRequestVersionResponse
import com.fireavert.yardi.shared.logic.data_models.YardiMiniCredentials
import kotlinx.coroutines.CoroutineDispatcher

class YardiCommonDataClientRepositoryImpl(
    private val dataSource: YardiCommonDataClientDataSource,
    private val preferences: Preferences,
    ioContext: CoroutineDispatcher,
    tokenRefreshService: TokenRefreshService
): YardiCommonDataClientRepository, ApiRepository(ioContext, tokenRefreshService) {

    override suspend fun getYardiCredentialsList(): Try<List<YardiMiniCredentials>> {
        return dataSource.getYardiCredentialsList(preferences.token)
    }

    override suspend fun getPropertyConfigurations(request: YardiClientPropertyIdRequest): Try<String> {
        return dataSource.getPropertyConfigurations(preferences.token, request)
            .map { it.response }
    }

    override suspend fun savePropertyDataBase(request: YardiSavePropertyDataRequest): Try<Boolean> {
        return dataSource.savePropertyDataBase(preferences.token, request)
    }

    override suspend fun testPing(credentialId: Int): Try<String> {
        return dataSource.testPing(preferences.token, YardiClientCredentialRequest(credentialId))
            .map { it.version }
    }

    override suspend fun testVersionNumber(credentialId: Int): Try<String> {
        return dataSource.testVersionNumber(preferences.token, YardiClientCredentialRequest(credentialId))
            .map { it.version }
    }

    override suspend fun testGetResidentData(request: YardiClientPropertyIdRequest): Try<String> {
        return dataSource.testGetResidentData(preferences.token, request)
            .map { it.residents.toString() }
    }

    override suspend fun testGetResidentByStatusData(request: YardiClientResidentByStatusRequest): Try<String> {
        return dataSource.testGetResidentByStatusData(preferences.token, request)
            .map { it.residents.toString() }
    }

    override suspend fun yardiGetPropertyList(request: YardiClientPropertyIdRequest): Try<List<YardiPropertyListItemData>> {
        return dataSource.yardiGetPropertyList(preferences.token, request)
            .map { it.propertyLists }
    }

    override suspend fun testGetResidentsByUnit(request: YardiClientGetResidentByUnitRequest): Try<String> {
        return dataSource.testGetResidentsByUnit(preferences.token, request)
            .map { it.units.toString() }
    }

    override suspend fun testGetTenantStatus(request: YardiClientPropertyIdRequest): Try<String> {
        return dataSource.testGetTenantStatus(preferences.token, request)
            .map { it.tenants }
    }

    override suspend fun testGetRateLimits(credentialId: Int): Try<String> {
        return dataSource.testGetRateLimits(preferences.token, YardiClientCredentialRequest(credentialId))
            .map { if (it.response.isNotEmpty()) it.response.toString() else "Message: No rate limits set." }
    }

    override suspend fun testGetUnitAttachments(credentialId: Int): Try<String> {
        return dataSource.testGetUnitAttachments(preferences.token, YardiClientCredentialRequest(credentialId))
            .map { it.residents.toString() }
    }

    override suspend fun yardiGetUnitData(request: YardiClientPropertyIdRequest): Try<PropertyWithUnitsData> {
        val result = dataSource.yardiGetUnitData(preferences.token, request)
            .map { it.unitList }
        return result
    }

    override suspend fun testGetResidentDataResidentialOnly(request: YardiClientGetResidentialDataResidentialOnlyRequest): Try<String> {
        return dataSource.testGetResidentDataResidentialOnly(preferences.token, request)
            .map { it.residents.toString() }
    }

    override suspend fun testGetServiceRequestVersionNumber(request: YardiClientCredentialRequest): Try<YardiServiceRequestVersionResponse> {
        return dataSource
            .testGetServiceRequestVersionNumber(preferences.token, request)
            .map { it }
    }

    override suspend fun testGetServiceRequestPropertyConfigurations(request: YardiClientPropertyIdRequest): Try<YardiServiceRequestPropertyConfigurationsResponse> {
        return dataSource
            .testGetServiceRequestPropertyConfigurations(preferences.token, request)
            .map { it }
    }

    override suspend fun testCreateEditServiceRequest(request: YardiClientServiceRequestCreateEditRequest): Try<YardiServiceRequestCreateEditResponse> {
        return dataSource
            .testCreateEditServiceRequest(preferences.token, request)
            .map { it }
    }

    override suspend fun testGetServiceRequests(request: YardiClientServiceRequestGetRequest): Try<YardiServiceRequestGetResponse> {
        return dataSource
            .testGetServiceRequests(preferences.token, request)
            .map { it }
    }

    override suspend fun importYardiUnitData(request: YardiClientImportUnitData): Try<YardiImportUnitDataResponse> {
        return dataSource.importYardiUnitData(preferences.token, request)
            .map { it }
    }
}